'use client'

import { useRef, useState } from 'react'
import { capitaliseEveryWord, uppercaseFirstLetter } from '@utils/stringUtils'
import {
  getStateAndCity,
  appendCodeToCommonCitiesMultipleStates,
  getAverageStatePrice,
  FuelType,
  getStateName,
} from '@helpers/fuelPageHelpers'
import { customReplaceAll } from '@utils/fuelStringUtils'
import { CITY, DIESEL, PETROL, PRICE, STATE } from './constants/texts'
import styles from './fuelTable.module.scss'

const FuelTable = ({
  data,
  fuelPageResponse,
  location,
  fuelType,
  parsedLocationIsState,
  getAllStatesIndiaResponse,
  mappedStateWithCities,
  stateCode,
}) => {
  const [localFuelType, setLocalFuelType] = useState(fuelType)
  const [hideContent, setHideContent] = useState(true)
  const mappedStateWithCitiesObj = mappedStateWithCities
  const FuelTableRef: React.RefObject<HTMLInputElement> = useRef(null)

  const listOfAllCities = location
    ? parsedLocationIsState
      ? mappedStateWithCitiesObj[getStateName(location)]
      : mappedStateWithCitiesObj[getStateAndCity(location, mappedStateWithCities, stateCode)]
    : []

  const listOfAllCitiesArray = Object.entries(listOfAllCities).map(([key, value]) => ({ [key]: value }))
  // eslint-disable-next-line no-param-reassign
  getAllStatesIndiaResponse = JSON.parse(getAllStatesIndiaResponse)
  const [fuelListOfCities] = useState<any>(listOfAllCitiesArray)
  const [fuelListOfStates] = useState<any>(getAllStatesIndiaResponse)
  const fuelListOfCitiesArrayLength =
    fuelListOfCities.length !== 0 && Object.keys(fuelListOfCities).length < 10 ? false : !(fuelListOfStates.length < 10)
  const [isLoadMore] = useState<any>(() => fuelListOfCitiesArrayLength)

  const handleTabs = redirectionUrl => {
    window.open(redirectionUrl, '_blank')
  }

  const fuelPricesCityList = fuelName =>
    fuelListOfCities?.map((val, index) => {
      const cityName = Object.keys(val)[0]
      const item = val[cityName]
      return (
        (location.toUpperCase() !== cityName || parsedLocationIsState) && (
          <div className={`${styles.fuelTableLinksContainer} ${hideContent && index > 9 ? styles.hidden : ''}`}>
            <div className={styles.fuelTableLiTag} key={`${item}${index}`}>
              <div
                className={styles.fuelTableAnchorTag}
                onClick={() =>
                  handleTabs(
                    `/fuel/${appendCodeToCommonCitiesMultipleStates(
                      `${fuelType}`,
                      capitaliseEveryWord(location),
                      capitaliseEveryWord(cityName),
                      fuelPageResponse?.attributes?.commonCitiesMultipleStates,
                      listOfAllCities
                    )}/`
                  )
                }>
                {uppercaseFirstLetter(cityName)}
              </div>
            </div>
            <div className={styles.fuelSubHeading}>{`₹ ${listOfAllCities[cityName][`${fuelName}Price`]}`}</div>
          </div>
        )
      )
    })

  const fuelPricesStateList = fuelName =>
    fuelListOfStates?.map((item, index) => {
      const stateVal = item.state_name.toLowerCase()
      const listAllCitiesInState = stateVal.includes('d&nh')
        ? mappedStateWithCitiesObj[stateVal.toUpperCase()]
        : mappedStateWithCitiesObj[capitaliseEveryWord(stateVal)]
      const averageStatePrice = getAverageStatePrice(listAllCitiesInState, fuelName)
      return (
        <div className={`${styles.fuelTableLinksContainer} ${hideContent && index > 9 ? styles.hidden : ''}`}>
          <div className={styles.fuelTableLiTag} key={`${item}${index}`}>
            <div
              className={styles.fuelTableAnchorTag}
              onClick={() =>
                handleTabs(`/fuel/${fuelName}-price-in-${item.state_name.toLowerCase().replace(/\s/g, '-')}/`)
              }>
              {uppercaseFirstLetter(item.state_name)}
            </div>
          </div>
          <div className={styles.fuelSubHeading}>{`₹ ${averageStatePrice}`}</div>
        </div>
      )
    })

  const fuelName = uppercaseFirstLetter(localFuelType)

  return (
    <div className={styles.fuelTableRootContainer}>
      <div className={styles.fuelTableHeadingContainer}>
        <div className={styles.fuelTableHeading}>
          {customReplaceAll(data?.data?.attributes?.tableHeading, '<fuelType>', uppercaseFirstLetter(fuelName))}
        </div>
        <div className={styles.fuelTypeContainer}>
          <div
            className={`${styles.fuelType} ${localFuelType === FuelType.Petrol && styles.currentFuel} `}
            onClick={() => setLocalFuelType(FuelType.Petrol)}>
            {PETROL}
          </div>
          <div
            className={`${styles.fuelType} ${localFuelType === FuelType.Diesel && styles.currentFuel} `}
            onClick={() => setLocalFuelType(FuelType.Diesel)}>
            {DIESEL}
          </div>
        </div>
      </div>
      {data?.data?.attributes?.tableContentType?.includes('City') && (
        <div className={styles.fuelTableMainContainer}>
          <div className={styles.rowHeading}>
            <div className={styles.fuelSubHeading}>{CITY}</div>
            <div className={styles.fuelSubHeading}>{PRICE}</div>
          </div>
          {/* petrol links */}
          <div className={`${styles.fuelTableUlTagPetrol} ${localFuelType !== FuelType.Petrol && styles.hidden}`}>
            {fuelPricesCityList(FuelType.Petrol)}
          </div>
          {/* diesel links */}
          <div className={`${styles.fuelTableUlTagDiesel} ${localFuelType !== FuelType.Diesel && styles.hidden}`}>
            {fuelPricesCityList(FuelType.Diesel)}
          </div>
        </div>
      )}
      {data?.data?.attributes?.tableContentType?.includes('State') && (
        <div className={styles.fuelTableMainContainer}>
          <div className={styles.rowHeading}>
            <div className={styles.fuelSubHeading}>{STATE}</div>
            <div className={styles.fuelSubHeading}>{PRICE}</div>
          </div>
          {/* petrol links */}
          <div className={`${styles.fuelTableUlTagPetrol} ${localFuelType !== FuelType.Petrol && styles.hidden}`}>
            {fuelPricesStateList(FuelType.Petrol)}
          </div>
          {/* diesel links */}
          <div className={`${styles.fuelTableUlTagDiesel} ${localFuelType !== FuelType.Diesel && styles.hidden}`}>
            {fuelPricesStateList(FuelType.Diesel)}
          </div>
        </div>
      )}
      {isLoadMore && (
        <div
          className={`${styles.fuelLoadMore} ${hideContent ? styles.hidden : ''}`}
          onClick={() => {
            if (!hideContent) {
              FuelTableRef?.current?.scrollIntoView({
                behavior: 'smooth',
              })
            }
            setHideContent(!hideContent)
          }}>
          {hideContent ? 'Load More' : 'Load Less'}
        </div>
      )}
    </div>
  )
}

export default FuelTable
